import {Component, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {AuthService} from "../../services/auth.service";
import {SubscriptionService} from "../../services/subscription.service";
import {Observable, Subject, Subscription, takeUntil} from "rxjs";
import {EventTypes} from "../../event-types";
import {SharedService} from "../../services/shared.service";
import {selectAITokens, selectCredits, selectError, selectLoading, selectUser} from "../../state/subscription/subscription.selectors";
import {Store} from "@ngrx/store";
import * as SubscriptionActions from "../../state/subscription/subscription.actions";
import {setIsLoggedIn, updateUser} from "../../state/auth/auth.actions";
import {Router, RouterLink, RouterLinkActive} from "@angular/router";
import {selectIsSidebarCollapsed} from "../../state/ui/ui.selectors";
import * as UiActions from '../../state/ui/ui.actions';
import {AsyncPipe, NgClass, NgForOf, NgIf, NgStyle, NgTemplateOutlet} from "@angular/common";
import {BillingService} from "../../services/billing.service";
import {selectIsDarkMode} from "../../state/ui/ui.selectors";
import { SafeHtmlPipe } from '../../pipes/safe-html.pipe';
import {NavigationItem, NavigationService} from "../../services/navigation.service";
import {resetState} from "../../state/global/global.actions";
import {SessionService} from "../../services/session.service";
import {PopupService} from "../../services/popup.service";
import {UiMigrationConfig, UiSwitchService} from "../../services/ui-switch.service";

interface SidebarItem {
  title: string;
  routerLink?: string;
  icon: string;
  onClick?: () => void;
  layouts: string[];
  badge?: string;
  section?: 'main' | 'bottom';
}

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  standalone: true,
  imports: [NgClass, NgStyle, NgIf, RouterLinkActive, RouterLink, AsyncPipe, NgForOf, NgTemplateOutlet, SafeHtmlPipe],
  styleUrls: ['./sidebar.component.scss']
})
export class SidebarComponent implements OnInit, OnDestroy {

  // Output for sidebar state
  @Output() collapsedChange = new EventEmitter<boolean>();
  @Output() closeMobile = new EventEmitter<void>();

  collapsed$: Observable<boolean>;
  @Input() collapsed = false;

  @Input() layout: string = 'main'; // Default to main layout

  // User and subscription data
  user: any = {};
  subscription: any;
  currentCredits = 0;
  isFading = false;
  isLoggedIn = false;

  // Add a flag to prevent API calls during logout
  private isLoggingOut = false;

  // Observables
  credits$: Observable<number | null>;
  aiTokens$: Observable<number | null>;
  user$: Observable<any | null>;
  loading$: Observable<boolean>;
  error$: Observable<any>;

  // Subject for unsubscribing from all observables
  private destroy$ = new Subject<void>();

  // Property to check if we're on desktop
  isDesktop = window.innerWidth >= 1024;

  // Delayed flag: true only after sidebar fully opens
  showFullLogo = true;
  private logoTimeout: any = null;

  isDarkMode$: Observable<boolean>;

  // New UI migration (Phase 1)
  uiMigration: UiMigrationConfig | null = null;
  isSwitchingUi = false;

  // Navigation items will be provided by the NavigationService
  sidebarItems: NavigationItem[] = [];

  constructor(
    private authService: AuthService,
    private subscriptionService: SubscriptionService,
    private store: Store,
    private sharedService: SharedService,
    private router: Router,
    private billingService: BillingService,
    private navigationService: NavigationService,
    private sessionService: SessionService,
    private popupService: PopupService,
    private uiSwitchService: UiSwitchService
  ) {
    this.collapsed$ = this.store.select(selectIsSidebarCollapsed);

    // New UI migration (Phase 1): "Try the new UI" link, shown while the backend flag is on.
    this.uiSwitchService.config().pipe(takeUntil(this.destroy$)).subscribe(config => {
      this.uiMigration = config;
    });

    // Get the dark mode state from the store
    this.isDarkMode$ = this.store.select(selectIsDarkMode);
  }

  // Helper method to execute onClick function from navigation item
  executeItemAction(item: NavigationItem): void {
    if (!this.isDesktop) {
      this.closeMobileSidebar();
    }

    if (item.onClick) {
      // Call the function directly - it's an arrow function that already has the correct context
      item.onClick();
    }
  }

  ngOnInit(): void {
    // Initialize isDesktop property
    this.isDesktop = window.innerWidth >= 1024;

    // Initialize user data
    this.authService.authUser()
      .pipe(takeUntil(this.destroy$))
      .subscribe(user => {
        this.user = user;
      });

    // Initialize subscription data
    this.subscriptionService.subscriptionData()
      .pipe(takeUntil(this.destroy$))
      .subscribe(subscription => {
        this.subscription = subscription;
      });

    // Track login state
    this.authService.isLoggedIn()
      .pipe(takeUntil(this.destroy$))
      .subscribe(isLoggedIn => {
        this.isLoggedIn = isLoggedIn;

        if (isLoggedIn && !this.isLoggingOut) {
          this.initializeStore();
          this.loadUserData();
        } else if (!isLoggedIn) {
          // Clear data when logged out
          this.store.dispatch(SubscriptionActions.clearSubscriptionData());
        }
      });

    // Listen for events
    this.sharedService.eventEmitter
      .pipe(takeUntil(this.destroy$))
      .subscribe(event => {
        // Don't process events if we're logging out
        if (!this.isLoggingOut && event.type === EventTypes.UPDATE_CREDITS) {
          this.loadUserData();
        }
      });

    // Subscribe to the store to keep local state in sync
    this.collapsed$.subscribe(isCollapsed => {
      clearTimeout(this.logoTimeout);
      if (isCollapsed) {
        // Closing: instantly hide full logo
        this.showFullLogo = false;
      } else {
        // Opening: wait for sidebar transition to finish, then show full logo
        this.logoTimeout = setTimeout(() => this.showFullLogo = true, 300);
      }
      this.collapsed = isCollapsed;
    });

    // Load navigation items from service
    this.sidebarItems = this.navigationService.getNavigationItems();
  }

  // Filter items based on the current layout
  get filteredItems(): SidebarItem[] {
    return this.sidebarItems.filter(item => item.layouts.includes(this.layout));
  }

  // Main nav items (everything not pinned to the bottom group).
  get mainItems(): SidebarItem[] {
    return this.filteredItems.filter(item => item.section !== 'bottom');
  }

  // Bottom nav group (Settings, Billing) — rendered separately, pinned below.
  get bottomItems(): SidebarItem[] {
    return this.filteredItems.filter(item => item.section === 'bottom');
  }

  // Method to handle window resize and update isDesktop property
  @HostListener('window:resize')
  onResize(): void {
    this.isDesktop = window.innerWidth >= 1024;
  }

  // Method to close mobile sidebar (emits event to parent)
  closeMobileSidebar(): void {
    this.closeMobile.emit();
  }

  ngOnDestroy(): void {
    // Clean up subscriptions
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Toggle sidebar collapsed state
   */
  toggleSidebar(): void {
    // Dispatch action to update the store
    this.store.dispatch(UiActions.toggleSidebarCollapsed({ isCollapsed: !this.collapsed }));
  }

  /**
   * Handle image loading errors
   */
  onImageError(event: Event): void {
    const imgElement = event.target as HTMLImageElement;
    imgElement.src = 'assets/images/human.svg';
  }

  /**
   * Signs out the current user and resets application state
   */
  signOut(): void {
    this.isLoggingOut = true;
    this.sessionService.signOut();
  }

  /** New UI migration: hand the session over to the new UI and go there. */
  async switchToNewUi(): Promise<void> {
    if (this.isSwitchingUi) return;
    this.isSwitchingUi = true;
    try {
      await this.uiSwitchService.switchToNewUi();
    } catch (e) {
      console.error('[Sidebar] Switching to the new UI failed', e);
      this.popupService.notify('Could not open the new UI. Please try again.', 'New UI', 'warning');
      this.isSwitchingUi = false;
    }
  }

  /**
   * Trigger the fade animation for credits
   */
  triggerFadeAnimation(): void {
    this.isFading = true;
    setTimeout(() => (this.isFading = false), 500);
  }

  /**
   * Initialize store selectors
   */
  private initializeStore(): void {
    this.credits$ = this.store.select(selectCredits);
    this.aiTokens$ = this.store.select(selectAITokens);
    this.user$ = this.store.select(selectUser);
    this.loading$ = this.store.select(selectLoading);
    this.error$ = this.store.select(selectError);

    // Subscribe to credits changes
    this.credits$
      .pipe(takeUntil(this.destroy$))
      .subscribe((newCredits) => {
        if (newCredits !== null) {
          if (this.currentCredits > 0 && newCredits < this.currentCredits) {
            this.triggerFadeAnimation();
          }
          this.currentCredits = newCredits;
        }
      });
  }

  /**
   * Load user data and credits
   * Now checks if we're in the process of logging out
   */
  private loadUserData(): void {
    // Skip loading data if not logged in or if we're in the process of logging out
    if (!this.isLoggedIn || this.isLoggingOut) return;

    // Load credits
    this.store.dispatch(SubscriptionActions.loadCredits());

    // Set up subscription handlers
    this.setupSubscriptions();
  }

  /**
   * Set up subscriptions to store selectors
   */
  private setupSubscriptions(): void {
    // Handle errors
    this.error$
      .pipe(takeUntil(this.destroy$))
      .subscribe(error => {
        // Don't handle errors if we're in the process of logging out
        if (error && !this.isLoggingOut) {
          if (error.status === 401) {
            this.handleUnauthorized();
          }
        }
      });

    // Handle user data changes
    this.user$
      .pipe(takeUntil(this.destroy$))
      .subscribe(storeUser => {
        if (storeUser && this.isUserModified(storeUser) && !this.isLoggingOut) {
          this.updateUserData(storeUser);
        }
      });
  }

  /**
   * Update user and subscription data
   */
  private updateUserData(storeUser: any): void {
    // Update user data
    const updatedUser = {
      ...this.user,
      name: storeUser.name,
      subscribed: storeUser.subscribed,
      on_trial: storeUser.on_trial,
      plan: storeUser.plan
    };

    this.store.dispatch(updateUser({ user: updatedUser }));

    // Update subscription data
    const updatedSubscription = {
      ...this.subscription,
      subscribed: storeUser.subscribed,
      on_trial: storeUser.on_trial
    };

    this.store.dispatch(SubscriptionActions.storeSubscriptionData(updatedSubscription));
  }

  /**
   * Handle unauthorized error (401)
   */
  private handleUnauthorized(): void {
    // If we're not already logging out, handle unauthorized
    if (!this.isLoggingOut) {
      this.isLoggingOut = true;
      this.store.dispatch(resetState());
      this.router.navigate(['/login']);
    }
  }

  /**
   * Check if user data has been modified in the store
   */
  private isUserModified(storeUser: any): boolean {
    return (
      this.user?.subscribed !== storeUser.subscribed ||
      this.user?.on_trial !== storeUser.on_trial ||
      JSON.stringify(this.user?.plan) !== JSON.stringify(storeUser.plan)
    );
  }

  openBillingPortal() {
    if (!this.isLoggingOut) {
      this.billingService.openBillingPortal();
    }
  }

}
